import { stackOrderNone, stackOffsetNone } from 'd3';
import { getCategorySortingOrderFromProductClass } from "@atlas-int/viz";
import { ProductClass, TradeFlow, TradeDirection } from "@atlas-int/data/types";
import { computeTotalSumByTradeFlow, getProductsForClass, MetadataMergeType } from "@atlas-int/data";
import { mergeDataWithTopLevelParent } from '../../../Utils';
import { MetadataFetchStatus, filterTradeData } from "@atlas-int/data";
import { createProductLookupIndex } from "@atlas-int/data";
import transformProducts from '../transformProducts';
import { sortProductsForRibbons } from '../Utils';
import { buildStackData, TransformedTradeDataItem } from './buildStackData';
import type { RawSeriesData, TradeValueScaledByYAxisOption, YearRangeForProductClass, ProductMetadataBundle } from '../types';
import type { LocationYearDatum, DeflatorDatum } from "@atlas-int/data/types";
import type { TradeDataItem as SharedTradeDataItem } from "@atlas-int/data/types";

/** A trade data item with dynamic properties from merges and transforms */
interface TradeDataItem extends SharedTradeDataItem {
  year: number;
  partnerId?: string;
  topLevelParent?: string;
}

export interface TransformProductsViewParams {
  data: TradeDataItem[];
  productClass: ProductClass;
  productLevel: number | undefined;
  productMetadata: ProductMetadataBundle;
  deflatorsMetadataStatus: string;
  deflatorsMetadata: DeflatorDatum[] | undefined;
  locationYearMetadataStatus: string;
  locationYearMetadata: LocationYearDatum[] | undefined;
  hiddenCategories: string[];
  yearMin: number;
  yearMax: number;
  yearRangeForProductClass: YearRangeForProductClass;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector: string;
  scaledTradeValueSelector: TradeValueScaledByYAxisOption;
  yAxisScalingOption: string;
  stackOrderFunction: typeof stackOrderNone;
  stackOffsetFunction: typeof stackOffsetNone;
  setYAxisDomain: (domain: { min: number; max: number }) => void;
}

export interface TransformViewResult {
  seriesData: RawSeriesData[] | undefined;
  totalSum: number | undefined;
  noDataToDisplay: boolean;
  stackKeysSet: Set<string> | undefined;
}

export const transformProductsView = ({
  data,
  productClass,
  productLevel,
  productMetadata,
  deflatorsMetadataStatus,
  deflatorsMetadata,
  locationYearMetadataStatus,
  locationYearMetadata,
  hiddenCategories,
  yearMin,
  yearMax,
  yearRangeForProductClass,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
  scaledTradeValueSelector,
  yAxisScalingOption,
  stackOrderFunction,
  stackOffsetFunction,
  setYAxisDomain,
}: TransformProductsViewParams): TransformViewResult => {
  let seriesData: RawSeriesData[] | undefined = undefined;
  let totalSum: number | undefined = undefined;
  let stackKeysSet: Set<string> | undefined = undefined;
  let noDataToDisplay: boolean = false;

  // Step 1: Use shared getProductsForClass
  const products = getProductsForClass(productClass, {
    hs92: productMetadata.hs92,
    hs12: productMetadata.hs12,
    hs22: productMetadata.hs22,
    sitc: productMetadata.sitc,
  });

  const deflators: DeflatorDatum[] | undefined =
    deflatorsMetadataStatus === MetadataFetchStatus.Success ? deflatorsMetadata : undefined;

  const locationYearPopulations: LocationYearDatum[] | undefined =
    locationYearMetadataStatus === MetadataFetchStatus.Success ? locationYearMetadata : undefined;

  if (products) {
    let dataWithTopLevelProductId = mergeDataWithTopLevelParent({
      data,
      mergeType: MetadataMergeType.Sector,
      productsMetadata: products,
    });

    let dataSortedByCategory;
    let sortingOrder: string[] = getCategorySortingOrderFromProductClass({
      productClass,
    });
    if (sortingOrder.length > 0) {
      dataSortedByCategory = [...dataWithTopLevelProductId].sort(
        (productA: TradeDataItem, productB: TradeDataItem) =>
          sortProductsForRibbons({ productA, productB, sortingOrder }),
      );
    } else {
      dataSortedByCategory = dataWithTopLevelProductId;
    }

    // Step 3: Use shared filterTradeData
    let filteredData = filterTradeData({
      data: dataSortedByCategory,
      hiddenCategories,
    });

    let filteredDataWithinYearLimits = filteredData.filter(
      (d) => d.year !== undefined && d.year >= yearMin! && d.year <= yearMax!,
    );

    totalSum =
      filteredDataWithinYearLimits.length > 0
        ? computeTotalSumByTradeFlow({
            data: filteredData.filter((d) => d.year === yearMax),
            tradeFlow,
            tradeDirection,
          })
        : 0;

    const transformedData = transformProducts({
      yAxisScalingOption: yAxisScalingOption,
      productLevel,
      untransformedData: filteredData as Parameters<typeof transformProducts>[0]["untransformedData"],
      products,
      deflators,
      locationYearPopulations,
      yearRangeForProductClass,
      tradeFlow,
    });

    if (transformedData) {
      // Step 4: Use createProductLookupIndex for O(1) lookups instead of .find()
      const productLookup = createProductLookupIndex(products);

      // Step 8: Use shared buildStackData
      const { series, stackKeysSet: keys } = buildStackData({
        transformedData: transformedData as unknown as TransformedTradeDataItem[],
        yearMin: yearMin!,
        yearMax: yearMax!,
        entityIdAccessor: (d) => d.productId,
        scaledTradeValueSelector,
        tradeDirectionSelector,
        stackOrderFunction,
        stackOffsetFunction,
        setYAxisDomain,
      });
      stackKeysSet = keys;

      const seriesWithMetadata: RawSeriesData[] = series.map((d) => {
        const productId = d.key;
        const metadata = productLookup.get(productId);
        const topLevelParent = metadata?.topParent?.productId;
        const topLevelParentMetadata = topLevelParent ? productLookup.get(topLevelParent) : undefined;

        return {
          key: productId,
          topLevelParent,
          topLevelParentName: topLevelParentMetadata?.nameShortEn,
          nameEn: metadata?.nameShortEn,
          productCode: metadata?.code,
          seriesData: [...d],
        };
      });

      seriesData = seriesWithMetadata;
    } else {
      noDataToDisplay = true;
    }
  }

  return { seriesData, totalSum, noDataToDisplay, stackKeysSet };
};
